import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import * as Sharing from 'expo-sharing';
import { AuthRequest } from 'expo-auth-session';
import { supabase } from '../lib/supabase';
import { apiFetch } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { DISPLAY_FONT, makeThemedStyles, radii, useTheme } from '../v10/theme';
import { Atmos, Field, GlassCard } from '../v10/ui';
import type { ToolProps } from '../olmps/ToolHost';
import { ScreenHelp } from '../v10/ScreenHelp';
import { hrmezHelp } from '../olmps/help/hrmezHelp';

// ---------------------------------------------------------------------------
// HRMEZ (Hermes) — mobile port of the capture pad + AI routing tool.
// Golden reference: src/app/hrmez/page.tsx (pad), src/app/hrmez/records/page.tsx
// (AI Memory / decisions log), src/components/settings/hrmez-settings.tsx
// (config surfaces: local folders, rules, drive connection).
//
// Ported (1:1 data model, adapted UI for phone width):
//   1. Pad capture + classification chips + routing (notes/tasks/calendar/
//      journal/discard/areas) — same tables, same routing writes as web.
//   2. AI auto-classification via POST /api/hrmez/classify (apiFetch, Bearer
//      auth works per MoyRi precedent; route itself only reads userId body
//      param, same contract web uses).
//   3. AI Memory panel — hrmez_decisions, read-only, paginated 50/page.
//   4. Local folders tree (hrmez_local_folders CRUD + P.A.R.T.S seeding),
//      recursively browsable to any depth (parent_id chains, breadcrumb nav
//      mirroring apps/mobile/src/tools/nemos/files.tsx's navigateToLocalFolder
//      / navigateToBreadcrumb pattern) and rules (hrmez_rules CRUD). File
//      upload/download for hrmez_local_files IS ported at any folder depth:
//      drilling into a folder lists its subfolders + files (same
//      `hrmez_local_files` table / `hrmez-files` storage bucket as web's
//      src/app/nemos/files/page.tsx and src/app/hrmez/hierarchy/page.tsx),
//      upload uses expo-document-picker's getDocumentAsync (multiple: true)
//      following web's `${userId}/${folderId}/${Date.now()}_${fileName}`
//      storage path convention, and opening a file requests a 60s signed URL
//      then hands it to Linking.openURL (falls back to expo-sharing's
//      shareAsync if a share sheet is preferred and available) — there is no
//      native <a download> equivalent, so this is the mobile-appropriate
//      substitute for web's blob-download flow.
//   5. Google Drive: real native OAuth via expo-auth-session's AuthRequest.
//      GET /api/hrmez/auth?userId=&mobile_redirect=olmps://hrmez-auth builds
//      the Google consent URL server-side; the server callback (owned by the
//      web team, src/app/api/hrmez/auth/callback/route.ts) now carries an
//      optional whitelisted mobile_redirect through `state` and, on success
//      or failure, redirects to `olmps://hrmez-auth?connected=true` or
//      `olmps://hrmez-auth?error=...` instead of the web /hrmez page. This
//      view opens that URL with AuthRequest.promptAsync(..., { url }) (which
//      wraps expo-web-browser's openAuthSessionAsync internally — importing
//      expo-web-browser directly here is NOT possible, it's only a nested
//      transitive dep of expo-auth-session, not a top-level package.json dep)
//      and waits for the olmps://hrmez-auth return, then re-checks
//      hrmez_drive_tokens. "Connect on web" remains as the error-path
//      fallback hint (see handleDriveConnectPress).
//   6. Run Now → POST /api/hrmez/cron, same as web's manual trigger button.
// ---------------------------------------------------------------------------

const MOBILE_OAUTH_REDIRECT = 'olmps://hrmez-auth';

// ── Types (mirror src/interfaces/hrmez.ts + inline web page interfaces) ──

interface SubcategoryItem {
  id: string;
  name: string;
  type: string;
}

interface Classification {
  id: string;
  display_text: string;
  color: string;
  full_name: string;
  target_type: string;
  target_component: string | null;
  clear_window_hours: number | null;
  sort_order: number;
  is_default: boolean;
  subcategories: SubcategoryItem[] | null;
}

interface PadEntry {
  id: string;
  content: string;
  raw_text: string;
  classification: string | null;
  status: string;
  routed_to: any;
  created_at: string;
}

interface HrmezDecision {
  id: string;
  filename: string;
  file_type: string | null;
  source_path: string;
  destination_path: string;
  reason: string | null;
  method: 'rule' | 'ai' | 'user_override';
  confidence_score: number | null;
  timestamp: string;
}

interface HrmezRule {
  id: string;
  pattern_type: 'filename_contains' | 'file_type' | 'keyword';
  pattern_value: string;
  destination_path: string;
  priority: number;
}

interface LocalFolder {
  id: string;
  user_id: string;
  parent_id: string | null;
  name: string;
}

interface BreadcrumbItem {
  id: string;
  name: string;
}

interface LocalFile {
  id: string;
  user_id: string;
  folder_id: string;
  name: string;
  storage_path: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DEFAULT_CLASSIFICATIONS: Omit<Classification, 'id'>[] = [
  { display_text: 'N', color: '#22c55e', full_name: 'Notes', target_type: 'component', target_component: 'notes', clear_window_hours: null, sort_order: 1, is_default: true, subcategories: null },
  { display_text: 'L', color: '#ef4444', full_name: 'List', target_type: 'component', target_component: 'tasks', clear_window_hours: null, sort_order: 2, is_default: true, subcategories: null },
  { display_text: 'J', color: '#a855f7', full_name: 'Clio Journal Entry', target_type: 'component', target_component: 'journal', clear_window_hours: null, sort_order: 3, is_default: true, subcategories: null },
  { display_text: 'D', color: '#84cc16', full_name: 'Discard', target_type: 'discard', target_component: null, clear_window_hours: 48, sort_order: 4, is_default: true, subcategories: null },
  { display_text: 'Cal', color: '#94a3b8', full_name: 'Calendar', target_type: 'component', target_component: 'calendar', clear_window_hours: null, sort_order: 5, is_default: true, subcategories: null },
];

// SHAPE sectors: Social, Health, Admin, Personal, Economic.
const DEFAULT_LOCAL_ROOTS = ['Social', 'Health', 'Admin', 'Personal', 'Economic'];
const PARTS_FOLDERS = ['Projects', 'Areas', 'Resources', 'Tasks', 'Systems'];

function getContrastColor(hex: string): string {
  // Text over an arbitrary user-chosen swatch colour — same in both modes.
  const h = (hex || '#888888').replace('#', '');
  if (h.length < 6) return '#ffffff';
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function HrmezTool(_props: ToolProps) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;

  // Classifications
  const [classifications, setClassifications] = useState<Classification[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [loadingClassifications, setLoadingClassifications] = useState(true);
  const [dropdownClassId, setDropdownClassId] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<SubcategoryItem | null>(null);

  // Editor state
  const [editorText, setEditorText] = useState('');
  const [currentEntryId, setCurrentEntryId] = useState<string | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Recents
  const [showRecents, setShowRecents] = useState(false);
  const [recentEntries, setRecentEntries] = useState<PadEntry[]>([]);

  // Routing
  const [isRouting, setIsRouting] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Panels
  const [panel, setPanel] = useState<'none' | 'memory' | 'settings'>('none');

  // Journal types (for classification J routing — mirrors web's useSettings())
  const [pmJournalTypeId, setPmJournalTypeId] = useState<string | null>(null);
  const [pmFirstPromptId, setPmFirstPromptId] = useState<string | null>(null);
  const [journalTypeMissing, setJournalTypeMissing] = useState(false);

  // Subcategory option sources
  const [taskLists, setTaskLists] = useState<{ id: string; name: string }[]>([]);
  const [noteFolders, setNoteFolders] = useState<{ id: string; name: string }[]>([]);

  const [error, setError] = useState<string | null>(null);

  const selectedClassification = useMemo(
    () => classifications.find((c) => c.id === selectedClassId) || null,
    [classifications, selectedClassId]
  );

  // -------------------------------------------------------------------------
  // Load classifications (seed defaults if none exist — mirrors web)
  // -------------------------------------------------------------------------
  const loadClassifications = useCallback(async () => {
    if (!userId) return;
    setLoadingClassifications(true);
    const { data, error: err } = await supabase
      .from('hrmez_classifications')
      .select('*')
      .eq('user_id', userId)
      .order('sort_order');

    if (err) {
      setError(err.message);
      setLoadingClassifications(false);
      return;
    }

    if (!data || data.length === 0) {
      const toInsert = DEFAULT_CLASSIFICATIONS.map((c) => ({ ...c, user_id: userId }));
      const { data: seeded, error: seedErr } = await supabase
        .from('hrmez_classifications')
        .insert(toInsert)
        .select();
      if (seedErr) {
        setError(seedErr.message);
      } else {
        setClassifications((seeded ?? []) as Classification[]);
      }
    } else {
      setClassifications(data as Classification[]);
    }
    setLoadingClassifications(false);
  }, [userId]);

  const loadRecents = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('hrmez_entries')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(20);
    if (data) setRecentEntries(data as PadEntry[]);
  }, [userId]);

  const loadJournalDefault = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('user_settings')
      .select('custom_journal_entry_types')
      .eq('user_id', userId)
      .maybeSingle();
    const types = (data?.custom_journal_entry_types ?? []) as { id: string; title?: string; prompts?: { id: string }[] }[];
    const pmType =
      types.find((t) => (t.title || '').trim().toLowerCase() === 'pm') ||
      types.find((t) => /pm/i.test(t.title || '')) ||
      types[0];
    if (pmType) {
      setPmJournalTypeId(pmType.id);
      setPmFirstPromptId(pmType.prompts?.[0]?.id ?? null);
      setJournalTypeMissing(false);
    } else {
      setJournalTypeMissing(true);
    }
  }, [userId]);

  const loadSubcategoryOptions = useCallback(async () => {
    if (!userId) return;
    const [listsRes, foldersRes] = await Promise.all([
      supabase.from('task_lists').select('id, name').eq('user_id', userId).order('name'),
      supabase.from('note_folders').select('id, name').eq('user_id', userId).order('name'),
    ]);
    if (listsRes.data) setTaskLists(listsRes.data as { id: string; name: string }[]);
    if (foldersRes.data) setNoteFolders(foldersRes.data as { id: string; name: string }[]);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    void loadClassifications();
    void loadRecents();
    void loadJournalDefault();
    void loadSubcategoryOptions();
  }, [userId, loadClassifications, loadRecents, loadJournalDefault, loadSubcategoryOptions]);

  // -------------------------------------------------------------------------
  // Auto-save to hrmez_entries (debounced 800ms, mirrors web)
  // -------------------------------------------------------------------------
  const autoSave = useCallback(
    async (text: string) => {
      if (!userId || !text.trim()) return;
      const entryData = {
        user_id: userId,
        content: text,
        raw_text: text,
        classification: selectedClassification?.full_name || null,
        status: 'pending',
        source: 'typed',
        updated_at: new Date().toISOString(),
      };
      if (currentEntryId) {
        await supabase.from('hrmez_entries').update(entryData).eq('id', currentEntryId);
      } else {
        const { data } = await supabase
          .from('hrmez_entries')
          .insert({ ...entryData, created_at: new Date().toISOString() })
          .select('id')
          .single();
        if (data) setCurrentEntryId(data.id);
      }
    },
    [userId, currentEntryId, selectedClassification]
  );

  const handleEditorChange = (text: string) => {
    setEditorText(text);
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => void autoSave(text), 800);
  };

  const clearEditor = useCallback(() => {
    setEditorText('');
    setCurrentEntryId(null);
    setSelectedClassId(null);
    setDropdownClassId(null);
    setSelectedSubcategory(null);
  }, []);

  // -------------------------------------------------------------------------
  // Routing — component targets (mirrors web's routeToComponent exactly)
  // -------------------------------------------------------------------------
  const routeToComponent = async (component: string, title: string) => {
    if (!userId) return;
    const now = new Date().toISOString();

    switch (component) {
      case 'notes': {
        const noteData: any = {
          user_id: userId,
          title,
          content: editorText,
          created_at: now,
          updated_at: now,
        };
        if (selectedSubcategory?.type === 'note_folder') {
          noteData.folder_id = selectedSubcategory.id;
        }
        await supabase.from('notes').insert(noteData);
        break;
      }
      case 'tasks': {
        let listId = selectedSubcategory?.type === 'task_list' ? selectedSubcategory.id : null;
        if (!listId) {
          const { data: lists } = await supabase
            .from('task_lists')
            .select('id')
            .eq('user_id', userId)
            .order('created_at')
            .limit(1);
          listId = lists?.[0]?.id || null;
        }
        if (listId) {
          const { data: listRow } = await supabase
            .from('task_lists')
            .select('olmps_sector')
            .eq('id', listId)
            .maybeSingle();
          const listSector = (listRow as { olmps_sector?: string | null } | null)?.olmps_sector ?? null;
          await supabase.from('tasks').insert({
            user_id: userId,
            list_id: listId,
            title,
            done: false,
            ...(listSector ? { sector: listSector } : {}),
          });
        }
        break;
      }
      case 'calendar': {
        await supabase.from('calendar_events').insert({
          user_id: userId,
          title,
          date: todayYmd(),
          start_time: '09:00',
          end_time: '09:30',
          notes: editorText,
          color: 'bg-primary',
          repeat: 'none',
          reminder: 'none',
          created_at: now,
          updated_at: now,
        });
        break;
      }
      case 'journal': {
        if (!pmJournalTypeId) {
          setError('No journal type configured — set a "PM" entry type in Journal settings first.');
          break;
        }
        const answers: Record<string, string> = pmFirstPromptId ? { [pmFirstPromptId]: editorText } : {};
        await supabase.from('journal_entries').insert({
          user_id: userId,
          date: todayYmd(),
          entry_type_id: pmJournalTypeId,
          answers,
          created_at: now,
          updated_at: now,
        });
        break;
      }
      default:
        break;
    }
  };

  const routeToTarget = async (classification: Classification, title: string) => {
    if (!userId) return;
    if (classification.target_type === 'discard') {
      const hours = classification.clear_window_hours || 48;
      const clearAfter = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
      if (currentEntryId) {
        await supabase
          .from('hrmez_entries')
          .update({ status: 'routed', disposable: true, clear_after: clearAfter })
          .eq('id', currentEntryId);
      }
      await supabase.from('hrmez_routing_log').insert({
        user_id: userId,
        entry_id: currentEntryId,
        from_classification: classification.full_name,
        routed_to_module: 'discard',
        method: 'user_manual',
      });
    } else if (classification.target_type === 'areas') {
      const routedTo: any = { module: 'areas' };
      if (selectedSubcategory) {
        routedTo.folder_id = selectedSubcategory.id;
        routedTo.folder_name = selectedSubcategory.name;
        routedTo.folder_type = selectedSubcategory.type;
      }
      if (currentEntryId) {
        await supabase.from('hrmez_entries').update({ status: 'routed', routed_to: routedTo }).eq('id', currentEntryId);
      }
      await supabase.from('hrmez_routing_log').insert({
        user_id: userId,
        entry_id: currentEntryId,
        from_classification: classification.full_name,
        routed_to_module: 'areas',
        routed_to_drive_path: selectedSubcategory?.name || null,
        method: 'user_manual',
      });
    } else if (classification.target_component) {
      await routeToComponent(classification.target_component, title);
      await supabase.from('hrmez_routing_log').insert({
        user_id: userId,
        entry_id: currentEntryId,
        from_classification: classification.full_name,
        routed_to_module: classification.target_component,
        method: 'user_manual',
      });
    }

    if (currentEntryId && classification.target_type !== 'discard') {
      await supabase
        .from('hrmez_entries')
        .update({ status: 'routed', routed_to: { module: classification.target_component || classification.target_type } })
        .eq('id', currentEntryId);
    }

    clearEditor();
    void loadRecents();
    setIsRouting(false);
  };

  const handleRoute = async () => {
    if (!userId || !editorText.trim()) return;
    setIsRouting(true);
    setError(null);

    try {
      const firstLine = editorText.split('\n')[0]?.trim() || 'Untitled';

      if (selectedClassification) {
        await routeToTarget(selectedClassification, firstLine);
        return;
      }

      // Undeclared — call the AI classifier (POST /api/hrmez/classify)
      try {
        const data = await apiFetch<{ success: boolean; result?: { suggestedTarget: string; confidence: number } }>(
          '/api/hrmez/classify',
          { method: 'POST', body: JSON.stringify({ userId, content: editorText }) }
        );

        const defaultTarget = 'notes';
        await routeToComponent(defaultTarget, firstLine);

        await supabase.from('hrmez_routing_log').insert({
          user_id: userId,
          entry_id: currentEntryId,
          from_classification: null,
          routed_to_module: defaultTarget,
          method: 'ai_decision',
          confidence_score: data.result?.confidence ?? null,
        });
      } catch {
        // Fallback to notes on classify failure (mirrors web's catch path)
        await routeToComponent('notes', firstLine);
      }

      if (currentEntryId) {
        await supabase.from('hrmez_entries').update({ status: 'routed', routed_to: { module: 'notes' } }).eq('id', currentEntryId);
      }

      clearEditor();
      void loadRecents();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Routing failed');
    } finally {
      setIsRouting(false);
    }
  };

  const handleDismiss = () => {
    if (editorText.trim()) {
      setShowDiscardConfirm(true);
    } else {
      clearEditor();
    }
  };

  const confirmDismiss = async () => {
    if (currentEntryId) {
      await supabase.from('hrmez_entries').update({ status: 'dismissed' }).eq('id', currentEntryId);
    }
    clearEditor();
    setShowDiscardConfirm(false);
  };

  const handleIconPress = (classId: string) => {
    if (selectedClassId === classId) {
      setSelectedClassId(null);
    } else {
      setSelectedClassId(classId);
      setSelectedSubcategory(null);
    }
    setDropdownClassId(null);
  };

  const handleIconLongPress = (classId: string) => {
    setDropdownClassId(dropdownClassId === classId ? null : classId);
    setSelectedClassId(classId);
  };

  const removeRecent = async (entryId: string) => {
    await supabase.from('hrmez_entries').delete().eq('id', entryId);
    setRecentEntries((prev) => prev.filter((e) => e.id !== entryId));
  };

  const dropdownClassification = dropdownClassId ? classifications.find((c) => c.id === dropdownClassId) ?? null : null;

  if (!userId) {
    return (
      <View style={styles.center}>
        <Text style={styles.emptyText}>Sign in to use HRMEZ.</Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView style={styles.root} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <Atmos>
      {error ? <Text style={styles.error}>{error}</Text> : null}

      {/* Header row: title + panel switches */}
      <View style={styles.headerRow}>
        <View style={styles.titleRow}>
          <Text style={styles.title}>H.R.M.E.Z</Text>
          <ScreenHelp help={hrmezHelp} />
        </View>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Pressable style={styles.headerBtn} onPress={() => setPanel(panel === 'memory' ? 'none' : 'memory')}>
            <Text style={styles.headerBtnText}>Memory</Text>
          </Pressable>
          <Pressable style={styles.headerBtn} onPress={() => setPanel(panel === 'settings' ? 'none' : 'settings')}>
            <Text style={styles.headerBtnText}>Folders &amp; Rules</Text>
          </Pressable>
        </View>
      </View>

      {/* Classification chip row */}
      <View style={styles.chipSection}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipRow}>
          {loadingClassifications ? (
            <ActivityIndicator color={c.blueText} />
          ) : (
            classifications.map((cls) => {
              const isSelected = selectedClassId === cls.id;
              const textColor = getContrastColor(cls.color);
              return (
                <Pressable
                  key={cls.id}
                  style={[
                    styles.chip,
                    { backgroundColor: cls.color },
                    isSelected && styles.chipSelected,
                  ]}
                  onPress={() => handleIconPress(cls.id)}
                  onLongPress={() => handleIconLongPress(cls.id)}
                >
                  <Text style={[styles.chipText, { color: textColor }]}>{cls.display_text}</Text>
                </Pressable>
              );
            })
          )}
        </ScrollView>

        {dropdownClassification ? (
          <GlassCard style={styles.dropdown}>
            {dropdownClassification.target_type === 'discard' ? (
              <Text style={styles.dropdownHint}>
                Discard: entry will auto-delete after {dropdownClassification.clear_window_hours || 48} hours.
              </Text>
            ) : (dropdownClassification.subcategories?.length ?? 0) === 0 ? (
              <Text style={styles.dropdownHint}>
                No subcategories configured for {dropdownClassification.full_name}. Add them on web Settings.
              </Text>
            ) : (
              <View style={styles.dropdownChipRow}>
                {(dropdownClassification.subcategories ?? []).map((sub) => {
                  const active = selectedSubcategory?.id === sub.id;
                  return (
                    <Pressable
                      key={sub.id}
                      style={[styles.subChip, active && styles.subChipActive]}
                      onPress={() => {
                        setSelectedSubcategory(active ? null : sub);
                        setDropdownClassId(null);
                      }}
                    >
                      <Text style={[styles.subChipText, active && styles.subChipTextActive]}>{sub.name}</Text>
                    </Pressable>
                  );
                })}
              </View>
            )}
          </GlassCard>
        ) : null}

        {selectedClassification ? (
          <Text style={styles.routingHint}>
            Routing to: <Text style={styles.routingHintStrong}>{selectedClassification.full_name}</Text>
            {selectedSubcategory ? <Text> › {selectedSubcategory.name}</Text> : null}
          </Text>
        ) : !loadingClassifications ? (
          <Text style={styles.routingHintItalic}>No icon selected — HRMEZ will auto-classify on send</Text>
        ) : null}

        {selectedClassification?.target_component === 'journal' && journalTypeMissing ? (
          <Text style={styles.warnText}>No "PM" journal entry type configured — journal routing will fail.</Text>
        ) : null}
      </View>

      {/* Pad editor */}
      <View style={styles.editorWrap}>
        <Field
          style={styles.editorField}
          inputStyle={styles.editor}
          multiline
          value={editorText}
          onChangeText={handleEditorChange}
          placeholder="Capture a thought…"
          textAlignVertical="top"
          autoFocus
        />
      </View>

      {/* Action bar */}
      <GlassCard style={styles.actionBar}>
        <Pressable
          style={styles.actionGhost}
          onPress={() => {
            setShowRecents((v) => !v);
            void loadRecents();
          }}
        >
          <Text style={styles.actionGhostText}>{showRecents ? '▲ Recents' : '▼ Recents'}</Text>
        </Pressable>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Pressable style={styles.actionGhost} onPress={handleDismiss} disabled={isRouting}>
            <Text style={styles.actionGhostText}>✕</Text>
          </Pressable>
          <Pressable
            style={[styles.actionPrimary, (!editorText.trim() || isRouting) && styles.actionPrimaryDisabled]}
            onPress={() => void handleRoute()}
            disabled={!editorText.trim() || isRouting}
          >
            {isRouting ? (
              <ActivityIndicator size="small" color={t.onGold} />
            ) : (
              <Text style={styles.actionPrimaryText}>✓ Send</Text>
            )}
          </Pressable>
        </View>
      </GlassCard>

      {/* Discard confirm */}
      {showDiscardConfirm ? (
        <GlassCard style={styles.confirmBar}>
          <Text style={styles.confirmText}>Discard this entry?</Text>
          <Pressable style={styles.confirmYes} onPress={() => void confirmDismiss()}>
            <Text style={styles.confirmYesText}>Yes</Text>
          </Pressable>
          <Pressable style={styles.confirmNo} onPress={() => setShowDiscardConfirm(false)}>
            <Text style={styles.confirmNoText}>No</Text>
          </Pressable>
        </GlassCard>
      ) : null}

      {/* Recents panel */}
      {showRecents ? (
        <GlassCard style={styles.recentsPanel}>
          <View style={styles.recentsHeader}>
            <Text style={styles.recentsTitle}>Recent Entries</Text>
            <Pressable onPress={() => setShowRecents(false)}>
              <Text style={styles.recentsClose}>✕</Text>
            </Pressable>
          </View>
          <ScrollView style={{ maxHeight: 260 }}>
            {recentEntries.length === 0 ? (
              <Text style={styles.emptyText}>No recent entries.</Text>
            ) : (
              recentEntries.map((entry) => {
                const firstLine = entry.raw_text?.split('\n')[0]?.substring(0, 60) || 'Untitled';
                const cls = classifications.find((c) => c.full_name === entry.classification);
                const routedModule = entry.routed_to?.module;
                return (
                  <View key={entry.id} style={styles.recentRow}>
                    {cls ? (
                      <View style={[styles.recentSwatch, { backgroundColor: cls.color }]}>
                        <Text style={[styles.recentSwatchText, { color: getContrastColor(cls.color) }]}>{cls.display_text}</Text>
                      </View>
                    ) : (
                      <View style={styles.recentSwatchEmpty} />
                    )}
                    <View style={{ flex: 1 }}>
                      <Text style={styles.recentLine} numberOfLines={1}>
                        {firstLine}
                      </Text>
                      <Text style={styles.recentMeta}>
                        {routedModule ? `→ ${routedModule} · ` : ''}
                        {new Date(entry.created_at).toLocaleDateString()}
                      </Text>
                    </View>
                    <Pressable onPress={() => void removeRecent(entry.id)}>
                      <Text style={styles.recentRemove}>✕</Text>
                    </Pressable>
                  </View>
                );
              })
            )}
          </ScrollView>
        </GlassCard>
      ) : null}

      <HrmezMemoryModal visible={panel === 'memory'} userId={userId} onClose={() => setPanel('none')} />
      <HrmezSettingsModal visible={panel === 'settings'} userId={userId} onClose={() => setPanel('none')} />
      </Atmos>
    </KeyboardAvoidingView>
  );
}

// ---------------------------------------------------------------------------
// AI Memory panel — hrmez_decisions, read-only, paginated 50/page
// (mirrors src/app/hrmez/records/page.tsx "Move Log" tab)
// ---------------------------------------------------------------------------
function HrmezMemoryModal({ visible, userId, onClose }: { visible: boolean; userId: string; onClose: () => void }) {
  const { c } = useTheme();
  const styles = useStyles();
  const [decisions, setDecisions] = useState<HrmezDecision[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 50;

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const { data } = await supabase
      .from('hrmez_decisions')
      .select('*')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    setDecisions((data ?? []) as HrmezDecision[]);
    setLoading(false);
  }, [userId, page]);

  useEffect(() => {
    if (visible) void load();
  }, [visible, load]);

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <Atmos style={styles.modalRoot}>
        <View style={styles.modalHeaderBar}>
          <Pressable onPress={onClose}>
            <Text style={styles.modalHeaderBtn}>Close</Text>
          </Pressable>
          <Text style={styles.modalHeaderTitle}>AI Memory</Text>
          <View style={{ width: 56 }} />
        </View>
        <ScrollView contentContainerStyle={styles.modalScrollContent}>
          {loading ? (
            <ActivityIndicator color={c.blueText} />
          ) : decisions.length === 0 ? (
            <Text style={styles.emptyText}>No routing decisions yet.</Text>
          ) : (
            decisions.map((d) => (
              <GlassCard key={d.id} style={styles.decisionCard}>
                <View style={styles.decisionHeaderRow}>
                  <Text style={styles.decisionFile} numberOfLines={1}>
                    {d.filename}
                  </Text>
                  <View style={[styles.methodBadge, d.method === 'rule' && styles.methodBadgeRule]}>
                    <Text style={styles.methodBadgeText}>{d.method}</Text>
                  </View>
                </View>
                <Text style={styles.decisionPath} numberOfLines={1}>
                  {d.source_path} → {d.destination_path}
                </Text>
                {d.reason ? (
                  <Text style={styles.decisionReason} numberOfLines={2}>
                    {d.reason}
                  </Text>
                ) : null}
                <Text style={styles.decisionMeta}>
                  {formatTimestamp(d.timestamp)}
                  {d.confidence_score != null ? ` · ${Math.round(d.confidence_score * 100)}%` : ''}
                </Text>
              </GlassCard>
            ))
          )}
        </ScrollView>
        <View style={styles.pagerRow}>
          <Pressable
            style={[styles.pagerBtn, page === 0 && styles.pagerBtnDisabled]}
            onPress={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            <Text style={styles.pagerBtnText}>Previous</Text>
          </Pressable>
          <Text style={styles.pagerLabel}>Page {page + 1}</Text>
          <Pressable
            style={[styles.pagerBtn, decisions.length < PAGE_SIZE && styles.pagerBtnDisabled]}
            onPress={() => setPage((p) => p + 1)}
            disabled={decisions.length < PAGE_SIZE}
          >
            <Text style={styles.pagerBtnText}>Next</Text>
          </Pressable>
        </View>
      </Atmos>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Folders & Rules settings panel — local folders CRUD + P.A.R.T.S seeding,
// rules CRUD, Drive connection status, Run Now.
// (mirrors src/components/settings/hrmez-settings.tsx "File Routing Settings")
// ---------------------------------------------------------------------------
export function HrmezSettingsModal({ visible, userId, onClose }: { visible: boolean; userId: string; onClose: () => void }) {
  const { t, c, neu } = useTheme();
  const styles = useStyles();
  const [localFolders, setLocalFolders] = useState<LocalFolder[]>([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');

  const [rules, setRules] = useState<HrmezRule[]>([]);
  const [ruleValue, setRuleValue] = useState('');
  const [ruleDest, setRuleDest] = useState('');

  const [isConnected, setIsConnected] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [runResult, setRunResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Recursive folder navigation state — mirrors apps/mobile/src/tools/nemos/
  // files.tsx's currentFolderId + breadcrumbs pattern (parent_id chains),
  // extended here to also carry files-at-this-level (folderFiles) so upload/
  // open/delete work at any depth, not just root.
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: 'root', name: 'Local Folders' }]);
  const [folderFiles, setFolderFiles] = useState<LocalFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const loadLocalFolders = useCallback(async (parentId: string | null) => {
    if (!userId) return;
    let query = supabase.from('hrmez_local_folders').select('*').eq('user_id', userId).order('name');
    query = parentId ? query.eq('parent_id', parentId) : query.is('parent_id', null);
    const { data, error: err } = await query;

    if (err) {
      setError(err.message);
      return;
    }

    if (!parentId && (!data || data.length === 0)) {
      const rootInserts = DEFAULT_LOCAL_ROOTS.map((name) => ({ user_id: userId, parent_id: null, name }));
      const { data: roots, error: seedErr } = await supabase.from('hrmez_local_folders').insert(rootInserts).select();
      if (seedErr || !roots) {
        setError(seedErr?.message ?? 'Failed to seed folders');
        return;
      }
      const subInserts = roots.flatMap((root) => PARTS_FOLDERS.map((name) => ({ user_id: userId, parent_id: root.id, name })));
      await supabase.from('hrmez_local_folders').insert(subInserts);
      setLocalFolders(roots as LocalFolder[]);
    } else {
      setLocalFolders((data ?? []) as LocalFolder[]);
    }
  }, [userId]);

  const loadFolderFiles = useCallback(async (folderId: string | null) => {
    if (!userId || !folderId) {
      setFolderFiles([]);
      return;
    }
    setLoadingFiles(true);
    const { data, error: err } = await supabase
      .from('hrmez_local_files')
      .select('*')
      .eq('user_id', userId)
      .eq('folder_id', folderId)
      .order('name');
    if (err) setError(err.message);
    setFolderFiles((data ?? []) as LocalFile[]);
    setLoadingFiles(false);
  }, [userId]);

  const navigateToLocalFolder = async (folder: LocalFolder) => {
    setCurrentFolderId(folder.id);
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
    await Promise.all([loadLocalFolders(folder.id), loadFolderFiles(folder.id)]);
  };

  const navigateToBreadcrumb = async (index: number) => {
    const crumb = breadcrumbs[index];
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
    const parentId = crumb.id === 'root' ? null : crumb.id;
    setCurrentFolderId(parentId);
    await Promise.all([loadLocalFolders(parentId), loadFolderFiles(parentId)]);
  };

  const loadRules = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase.from('hrmez_rules').select('*').eq('user_id', userId).order('priority', { ascending: false });
    if (data) setRules(data as HrmezRule[]);
  }, [userId]);

  const checkConnection = useCallback(async () => {
    if (!userId) return;
    setCheckingConnection(true);
    const { data } = await supabase.from('hrmez_drive_tokens').select('id, expiry').eq('user_id', userId).maybeSingle();
    setIsConnected(!!data);
    setCheckingConnection(false);
  }, [userId]);

  useEffect(() => {
    if (visible) {
      setCurrentFolderId(null);
      setBreadcrumbs([{ id: 'root', name: 'Local Folders' }]);
      void loadLocalFolders(null);
      void loadFolderFiles(null);
      void loadRules();
      void checkConnection();
      setRunResult(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, loadRules, checkConnection]);

  const handleAddLocalFolder = async () => {
    const name = newFolderName.trim();
    if (!name || !userId) return;
    const { data, error: err } = await supabase
      .from('hrmez_local_folders')
      .insert({ user_id: userId, parent_id: currentFolderId, name })
      .select()
      .single();
    if (err) {
      setError(err.message);
      return;
    }
    // P.A.R.T.S subfolders are only seeded for root-level folders (mirrors
    // the pre-existing top-level-only seeding behavior; nested "Add" calls
    // just create a plain subfolder, matching nemos/files.tsx's handleCreateFolder).
    if (data && !currentFolderId) {
      const subInserts = PARTS_FOLDERS.map((n) => ({ user_id: userId, parent_id: data.id, name: n }));
      await supabase.from('hrmez_local_folders').insert(subInserts);
    }
    setNewFolderName('');
    void loadLocalFolders(currentFolderId);
  };

  const handleRenameLocalFolder = async (folderId: string) => {
    const name = editingFolderName.trim();
    if (!name) return;
    await supabase.from('hrmez_local_folders').update({ name }).eq('id', folderId);
    setEditingFolderId(null);
    setEditingFolderName('');
    void loadLocalFolders(currentFolderId);
  };

  const handleDeleteLocalFolder = (folder: LocalFolder) => {
    Alert.alert('Delete folder?', `"${folder.name}" and its subfolders will be permanently removed.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          await supabase.from('hrmez_local_folders').delete().eq('id', folder.id);
          void loadLocalFolders(currentFolderId);
        },
      },
    ]);
  };

  // -------------------------------------------------------------------------
  // Local files — hrmez_local_files rows + hrmez-files storage bucket, same
  // conventions as web's src/app/nemos/files/page.tsx handleFileUpload /
  // handleDownloadFile. Files list follows currentFolderId (see
  // loadFolderFiles / navigateToLocalFolder / navigateToBreadcrumb above), so
  // this works at any recursion depth, not just root.
  // -------------------------------------------------------------------------
  const handleUploadFile = async (folderId: string) => {
    if (!userId || isUploadingFile) return;
    const result = await DocumentPicker.getDocumentAsync({ type: '*/*', multiple: true, copyToCacheDirectory: true });
    if (result.canceled || !result.assets?.length) return;
    setIsUploadingFile(true);
    try {
      for (const asset of result.assets) {
        const fileName = asset.name || `file_${Date.now()}`;
        const storagePath = `${userId}/${folderId}/${Date.now()}_${fileName}`;
        const res = await fetch(asset.uri);
        const arrayBuffer = await res.arrayBuffer();
        const contentType = asset.mimeType || 'application/octet-stream';
        const { error: uploadError } = await supabase.storage.from('hrmez-files').upload(storagePath, arrayBuffer, { contentType });
        if (uploadError) {
          setError(`${fileName}: ${uploadError.message}`);
          continue;
        }
        const { error: dbError } = await supabase.from('hrmez_local_files').insert({
          user_id: userId,
          folder_id: folderId,
          name: fileName,
          storage_path: storagePath,
          mime_type: contentType,
          size_bytes: asset.size || arrayBuffer.byteLength,
        });
        if (dbError) setError(`Failed to save record for ${fileName}`);
      }
      await loadFolderFiles(folderId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleOpenFile = async (file: LocalFile) => {
    const { data, error: err } = await supabase.storage.from('hrmez-files').createSignedUrl(file.storage_path, 60);
    if (err || !data?.signedUrl) {
      setError(err?.message || 'Could not open file.');
      return;
    }
    try {
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(data.signedUrl, { dialogTitle: file.name });
        return;
      }
    } catch {
      // fall through to Linking below — Sharing.shareAsync only reliably
      // handles local file URIs on some platforms; a remote https URL is
      // best handed to the system browser/share sheet via Linking anyway.
    }
    const canOpen = await Linking.canOpenURL(data.signedUrl);
    if (!canOpen) {
      setError('No app available to open this file.');
      return;
    }
    await Linking.openURL(data.signedUrl);
  };

  const handleDeleteFile = (file: LocalFile) => {
    Alert.alert('Delete file?', `"${file.name}" will be permanently deleted.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          await supabase.storage.from('hrmez-files').remove([file.storage_path]);
          await supabase.from('hrmez_local_files').delete().eq('id', file.id);
          void loadFolderFiles(file.folder_id);
        },
      },
    ]);
  };

  const handleAddRule = async () => {
    if (!userId || !ruleValue.trim() || !ruleDest.trim()) return;
    await supabase.from('hrmez_rules').insert({
      user_id: userId,
      pattern_type: 'filename_contains',
      pattern_value: ruleValue.trim(),
      destination_path: ruleDest.trim(),
      priority: rules.length,
    });
    setRuleValue('');
    setRuleDest('');
    void loadRules();
  };

  const handleDeleteRule = (rule: HrmezRule) => {
    Alert.alert('Delete rule?', undefined, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          await supabase.from('hrmez_rules').delete().eq('id', rule.id);
          void loadRules();
        },
      },
    ]);
  };

  // ---------------------------------------------------------------------
  // Google Drive OAuth — native connect via expo-auth-session.
  //
  // GET /api/hrmez/auth?userId=&mobile_redirect=olmps://hrmez-auth builds the
  // Google consent URL server-side, carrying mobile_redirect through `state`
  // (see src/app/api/hrmez/auth/route.ts). AuthRequest.promptAsync(discovery,
  // { url }) opens that pre-built URL directly (bypassing AuthRequest's own
  // URL construction — we don't want it re-deriving client_id/redirect_uri,
  // the server already built the full authorize URL) and waits for the
  // olmps://hrmez-auth return. `discovery.authorizationEndpoint` is unused
  // when `url` is supplied, but AuthRequest requires a non-empty redirectUri
  // at construction time, so it's set to the deep link we expect back.
  //
  // Note: `AuthRequest.parseReturnUrl` would flag `state_mismatch` here
  // because its self-generated `state` never matches the server's userId-
  // bound state — that's expected and irrelevant; the server round-trips its
  // own CSRF state independently, and this call only cares about the actual
  // `connected` / `error` query params on the returned URL, read directly
  // below rather than trusting `result.type`.
  // ---------------------------------------------------------------------
  const [isConnectingDrive, setIsConnectingDrive] = useState(false);

  const openWebForDriveConnect = () => {
    Alert.alert(
      'Connect on web',
      'Google Drive OAuth must be completed on the web app (syqi web /hrmez → Settings → File Routing Settings) — the redirect flow lands back on a web page, not this app.'
    );
  };

  const handleConnectDrive = async () => {
    if (!userId || isConnectingDrive) return;
    setIsConnectingDrive(true);
    setError(null);
    try {
      const { url } = await apiFetch<{ url: string }>(
        `/api/hrmez/auth?userId=${encodeURIComponent(userId)}&mobile_redirect=${encodeURIComponent(MOBILE_OAUTH_REDIRECT)}`
      );

      const request = new AuthRequest({
        clientId: 'unused', // server already embedded the real client_id in `url`
        redirectUri: MOBILE_OAUTH_REDIRECT,
        usePKCE: false,
      });
      const result = await request.promptAsync({ authorizationEndpoint: url }, { url });

      if (result.type === 'cancel' || result.type === 'dismiss') {
        setIsConnectingDrive(false);
        return;
      }
      if (result.type !== 'success' && result.type !== 'error') {
        setError('Drive connection did not complete.');
        setIsConnectingDrive(false);
        return;
      }

      const params = 'params' in result ? result.params : {};
      if (params.error) {
        setError(`Drive connection failed: ${params.error}`);
      } else if (params.connected !== 'true') {
        setError('Drive connection did not complete.');
      }
      await checkConnection();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Drive connection failed');
    } finally {
      setIsConnectingDrive(false);
    }
  };

  const handleRunNow = async () => {
    if (!userId) return;
    setIsRunning(true);
    setRunResult(null);
    setError(null);
    try {
      const data = await apiFetch<{ success: boolean; result?: { processed?: number; moved?: number; flagged?: number } }>(
        '/api/hrmez/cron',
        { method: 'POST', body: JSON.stringify({ userId }) }
      );
      if (data.success) {
        const r = data.result ?? {};
        setRunResult(`Processed: ${r.processed ?? 0}, Moved: ${r.moved ?? 0}, Flagged: ${r.flagged ?? 0}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Run failed');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <Atmos style={styles.modalRoot}>
        <View style={styles.modalHeaderBar}>
          <Pressable onPress={onClose}>
            <Text style={styles.modalHeaderBtn}>Close</Text>
          </Pressable>
          <Text style={styles.modalHeaderTitle}>Folders &amp; Rules</Text>
          <View style={{ width: 56 }} />
        </View>
        <ScrollView contentContainerStyle={styles.modalScrollContent}>
          {error ? <Text style={styles.error}>{error}</Text> : null}

          {/* Drive connection status */}
          <Text style={styles.sectionTitle}>Google Drive Connection</Text>
          <GlassCard style={styles.driveCard}>
            {checkingConnection ? (
              <ActivityIndicator color={c.blueText} />
            ) : (
              <View style={styles.rowBetween}>
                <Text style={isConnected ? styles.connectedText : styles.disconnectedText}>
                  {isConnected ? '● Connected' : '○ Not connected'}
                </Text>
                {isConnected ? null : (
                  <Pressable
                    style={styles.smallOutlineBtn}
                    onPress={() => void handleConnectDrive()}
                    disabled={isConnectingDrive}
                  >
                    {isConnectingDrive ? (
                      <ActivityIndicator size="small" color={c.blueText} />
                    ) : (
                      <Text style={styles.smallOutlineBtnText}>Connect</Text>
                    )}
                  </Pressable>
                )}
              </View>
            )}
            {!checkingConnection && !isConnected ? (
              <Pressable onPress={openWebForDriveConnect} style={{ marginTop: 8 }}>
                <Text style={styles.smallLink}>Trouble connecting? Connect on web instead</Text>
              </Pressable>
            ) : null}
          </GlassCard>

          {/* Run Now */}
          {isConnected ? (
            <>
              <Pressable style={styles.runBtn} onPress={() => void handleRunNow()} disabled={isRunning}>
                {isRunning ? <ActivityIndicator size="small" color={t.onGold} /> : <Text style={styles.runBtnText}>Run Now</Text>}
              </Pressable>
              {runResult ? <Text style={styles.runResultText}>{runResult}</Text> : null}
            </>
          ) : null}

          {/* Local folders — recursive browse (parent_id chains), mirrors
              apps/mobile/src/tools/nemos/files.tsx's breadcrumb navigation. */}
          <Text style={styles.sectionTitle}>Local Folders</Text>
          <Text style={styles.hintText}>
            Root folders get P.A.R.T.S subfolders (Projects, Areas, Resources, Tasks, Systems). Tap a folder to browse
            into it; upload/open/delete work at any depth.
          </Text>

          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.breadcrumbScroll}>
            {breadcrumbs.map((crumb, i) => (
              <Pressable key={crumb.id} onPress={() => void navigateToBreadcrumb(i)} style={styles.breadcrumbItem}>
                <Text style={[styles.breadcrumbText, i === breadcrumbs.length - 1 && styles.breadcrumbTextActive]}>
                  {crumb.name}
                </Text>
                {i < breadcrumbs.length - 1 ? <Text style={styles.breadcrumbSep}>›</Text> : null}
              </Pressable>
            ))}
          </ScrollView>

          {localFolders.map((folder) => (
            <GlassCard key={folder.id} style={styles.folderRow}>
              {editingFolderId === folder.id ? (
                <>
                  <TextInput
                    style={[styles.textInput, { flex: 1 }]}
                    value={editingFolderName}
                    onChangeText={setEditingFolderName}
                    autoFocus
                    onSubmitEditing={() => void handleRenameLocalFolder(folder.id)}
                  />
                  <Pressable style={styles.smallOutlineBtn} onPress={() => void handleRenameLocalFolder(folder.id)}>
                    <Text style={styles.smallOutlineBtnText}>Save</Text>
                  </Pressable>
                  <Pressable style={styles.smallGhostBtn} onPress={() => setEditingFolderId(null)}>
                    <Text style={styles.smallGhostBtnText}>Cancel</Text>
                  </Pressable>
                </>
              ) : (
                <>
                  <Pressable style={{ flex: 1 }} onPress={() => void navigateToLocalFolder(folder)}>
                    <Text style={styles.folderName}>📁 {folder.name}</Text>
                  </Pressable>
                  <Pressable
                    onPress={() => {
                      setEditingFolderId(folder.id);
                      setEditingFolderName(folder.name);
                    }}
                  >
                    <Text style={styles.smallLink}>Edit</Text>
                  </Pressable>
                  <Pressable onPress={() => handleDeleteLocalFolder(folder)}>
                    <Text style={styles.smallLinkDanger}>Delete</Text>
                  </Pressable>
                </>
              )}
            </GlassCard>
          ))}

          <View style={styles.addRow}>
            <TextInput
              style={styles.addInput}
              value={newFolderName}
              onChangeText={setNewFolderName}
              placeholder={currentFolderId ? 'New subfolder name…' : 'New folder name…'}
              placeholderTextColor={c.slate}
              onSubmitEditing={() => void handleAddLocalFolder()}
            />
            <Pressable style={styles.addBtn} onPress={() => void handleAddLocalFolder()} disabled={!newFolderName.trim()}>
              <Text style={styles.addBtnText}>Add</Text>
            </Pressable>
          </View>

          {/* Files at the current folder level — only shown once inside a
              folder (root has no files of its own, only child folders). */}
          {currentFolderId ? (
            <GlassCard style={styles.filesPanel}>
              <View style={styles.rowBetween}>
                <Text style={styles.filesPanelTitle}>Files</Text>
                <Pressable
                  style={styles.smallOutlineBtn}
                  onPress={() => void handleUploadFile(currentFolderId)}
                  disabled={isUploadingFile}
                >
                  <Text style={styles.smallOutlineBtnText}>{isUploadingFile ? 'Uploading…' : '+ Upload'}</Text>
                </Pressable>
              </View>
              {loadingFiles ? (
                <ActivityIndicator color={c.blueText} style={{ marginVertical: 8 }} />
              ) : folderFiles.length === 0 ? (
                <Text style={styles.emptyText}>No files in this folder.</Text>
              ) : (
                folderFiles.map((file) => (
                  <GlassCard key={file.id} style={styles.fileRow}>
                    <Pressable style={{ flex: 1 }} onPress={() => void handleOpenFile(file)}>
                      <Text style={styles.fileName} numberOfLines={1}>📎 {file.name}</Text>
                      <Text style={styles.fileMeta}>{formatFileSize(file.size_bytes)} · {file.mime_type}</Text>
                    </Pressable>
                    <Pressable onPress={() => handleDeleteFile(file)}>
                      <Text style={styles.smallLinkDanger}>Delete</Text>
                    </Pressable>
                  </GlassCard>
                ))
              )}
            </GlassCard>
          ) : null}

          {/* Rules */}
          <Text style={styles.sectionTitle}>Sorting Rules</Text>
          <Text style={styles.hintText}>Filename-contains patterns override default P→T→R→S→A routing.</Text>
          {rules.map((rule) => (
            <GlassCard key={rule.id} style={styles.ruleRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.rulePattern} numberOfLines={1}>
                  "{rule.pattern_value}" → {rule.destination_path}
                </Text>
                <Text style={styles.ruleMeta}>{rule.pattern_type.replace('_', ' ')} · priority {rule.priority}</Text>
              </View>
              <Pressable onPress={() => handleDeleteRule(rule)}>
                <Text style={styles.smallLinkDanger}>Delete</Text>
              </Pressable>
            </GlassCard>
          ))}
          <GlassCard style={styles.ruleFormCard}>
            <TextInput
              style={styles.textInput}
              value={ruleValue}
              onChangeText={setRuleValue}
              placeholder="Filename contains…"
              placeholderTextColor={c.slate}
            />
            <TextInput
              style={styles.textInput}
              value={ruleDest}
              onChangeText={setRuleDest}
              placeholder="Destination path (e.g. 5 Econ/5R Resources)"
              placeholderTextColor={c.slate}
            />
            <Pressable
              style={styles.addBtn}
              onPress={() => void handleAddRule()}
              disabled={!ruleValue.trim() || !ruleDest.trim()}
            >
              <Text style={styles.addBtnText}>Add Rule</Text>
            </Pressable>
          </GlassCard>
        </ScrollView>
      </Atmos>
    </Modal>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  root: { flex: 1 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  error: { color: c.danger, fontSize: 12, paddingHorizontal: 16, paddingTop: 8 },
  emptyText: { fontSize: 13, color: c.slate, fontStyle: 'italic', textAlign: 'center', paddingVertical: 16 },

  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 6,
  },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  title: { fontSize: 18, fontWeight: '900', color: c.blueText, letterSpacing: 0.5, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  headerBtn: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingHorizontal: 10,
    paddingVertical: 6,
    ...neu.raisedSm,
  },
  headerBtnText: { fontSize: 11, fontWeight: '700', color: c.blueText },

  chipSection: { paddingHorizontal: 16, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: c.glassRim },
  chipRow: { flexDirection: 'row', gap: 8, paddingVertical: 6 },
  chip: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipSelected: { borderWidth: 2, borderColor: t.goldBright, transform: [{ scale: 1.08 }], boxShadow: `0 0 12px ${t.goldGlow}` },
  chipText: { fontSize: 12, fontWeight: '900' },

  dropdown: {
    marginTop: 6,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 10,
    ...neu.raised,
  },
  dropdownHint: { fontSize: 11, color: c.slate, fontStyle: 'italic' },
  dropdownChipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  subChip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    ...neu.raisedSm,
  },
  subChipActive: { backgroundColor: t.gold, boxShadow: `0 0 10px ${t.goldGlow}` },
  subChipText: { fontSize: 12, fontWeight: '600', color: c.text },
  subChipTextActive: { color: t.onGold },

  routingHint: { fontSize: 11, color: c.slate, marginTop: 6 },
  routingHintStrong: { fontWeight: '800', color: c.text },
  routingHintItalic: { fontSize: 11, color: c.slate, fontStyle: 'italic', marginTop: 6 },
  warnText: { fontSize: 11, color: c.danger, marginTop: 4 },

  editorWrap: { flex: 1, paddingHorizontal: 16, paddingTop: 10 },
  editorField: { flex: 1 },
  editor: { flex: 1, fontSize: 16, color: c.text, lineHeight: 22 },

  actionBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: t.glassStrong,
    ...neu.raised,
  },
  actionGhost: { paddingHorizontal: 10, paddingVertical: 8 },
  actionGhostText: { fontSize: 13, fontWeight: '700', color: c.slate },
  actionPrimary: {
    backgroundColor: t.gold,
    borderRadius: radii.tile,
    paddingHorizontal: 18,
    paddingVertical: 9,
    boxShadow: `0 4px 14px ${t.goldGlow}, inset 0 1px 0 ${t.goldBright}`,
  },
  actionPrimaryDisabled: { opacity: 0.5 },
  actionPrimaryText: { color: t.onGold, fontWeight: '800', fontSize: 13 },

  confirmBar: {
    position: 'absolute',
    bottom: 60,
    left: 16,
    right: 16,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    ...neu.raised,
  },
  confirmText: { flex: 1, fontSize: 13, color: c.text },
  confirmYes: { backgroundColor: c.danger, borderRadius: radii.tile, paddingHorizontal: 12, paddingVertical: 6 },
  confirmYesText: { color: c.white, fontWeight: '700', fontSize: 12 },
  confirmNo: { borderRadius: radii.tile, paddingHorizontal: 12, paddingVertical: 6, borderWidth: 1, borderColor: c.glassRim },
  confirmNoText: { color: c.text, fontWeight: '700', fontSize: 12 },

  recentsPanel: {
    position: 'absolute',
    bottom: 56,
    left: 0,
    right: 0,
    backgroundColor: t.glassStrong,
    paddingBottom: 8,
    ...neu.raisedLg,
  },
  recentsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
  },
  recentsTitle: { fontSize: 13, fontWeight: '800', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  recentsClose: { fontSize: 14, color: c.slate },
  recentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: t.lineFaint,
  },
  recentSwatch: { width: 20, height: 20, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  recentSwatchText: { fontSize: 8, fontWeight: '900' },
  recentSwatchEmpty: { width: 20, height: 20, borderRadius: 10, backgroundColor: c.glassRim },
  recentLine: { fontSize: 13, color: c.text },
  recentMeta: { fontSize: 11, color: c.slate },
  recentRemove: { fontSize: 13, color: c.slate, paddingHorizontal: 4 },

  // Modal shared styles
  modalRoot: { flex: 1 },
  modalHeaderBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: t.glassStrong,
    ...neu.raised,
  },
  modalHeaderBtn: { fontSize: 14, color: c.blueText, fontWeight: '700', width: 56 },
  modalHeaderTitle: { fontSize: 16, fontWeight: '900', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  modalScrollContent: { padding: 16, gap: 10, paddingBottom: 48 },

  sectionTitle: { fontSize: 14, fontWeight: '900', color: c.blueText, marginTop: 16, marginBottom: 4, letterSpacing: 0.3, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  hintText: { fontSize: 11, color: c.slate, marginBottom: 6 },
  rowBetween: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },

  driveCard: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 12,
    ...neu.raised,
  },
  connectedText: { color: t.onPace, fontWeight: '700', fontSize: 13 },
  disconnectedText: { color: c.slate, fontWeight: '700', fontSize: 13 },
  smallOutlineBtn: { backgroundColor: t.glass, borderRadius: radii.tile, paddingHorizontal: 10, paddingVertical: 6, ...neu.raisedSm },
  smallOutlineBtnText: { color: c.blueText, fontWeight: '700', fontSize: 12 },
  smallGhostBtn: { paddingHorizontal: 10, paddingVertical: 6 },
  smallGhostBtnText: { color: c.slate, fontWeight: '600', fontSize: 12 },
  smallLink: { color: c.blueText, fontWeight: '700', fontSize: 12 },
  smallLinkDanger: { color: c.danger, fontWeight: '700', fontSize: 12 },

  runBtn: {
    backgroundColor: c.blue,
    borderRadius: radii.tile,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 10,
  },
  runBtnText: { color: t.onGold, fontWeight: '800', fontSize: 13 },
  runResultText: { fontSize: 12, color: c.slate, marginTop: 6, textAlign: 'center' },

  breadcrumbScroll: { flexGrow: 0, marginTop: 4 },
  breadcrumbItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  breadcrumbText: { fontSize: 12, color: c.slate, paddingVertical: 4 },
  breadcrumbTextActive: { color: c.text, fontWeight: '800' },
  breadcrumbSep: { fontSize: 12, color: c.slate, marginHorizontal: 4 },

  folderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 10,
    marginTop: 6,
    ...neu.raised,
  },
  folderName: { flex: 1, fontSize: 14, color: c.text, fontWeight: '600' },

  filesPanel: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 10,
    marginTop: 4,
    marginLeft: 12,
    gap: 4,
    ...neu.raised,
  },
  filesPanelTitle: { fontSize: 12, fontWeight: '800', color: c.blueText, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  fileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 8,
    marginTop: 4,
    ...neu.raised,
  },
  fileName: { fontSize: 13, color: c.text, fontWeight: '600' },
  fileMeta: { fontSize: 10, color: c.slate, marginTop: 2 },

  addRow: { flexDirection: 'row', gap: 8, marginTop: 10 },
  addInput: {
    flex: 1,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: c.text,
    ...neu.insetDeep,
  },
  addBtn: { backgroundColor: c.blue, borderRadius: radii.tile, paddingHorizontal: 16, justifyContent: 'center', ...neu.raisedSm },
  addBtnText: { color: t.onGold, fontWeight: '800', fontSize: 13 },

  textInput: {
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 13,
    color: c.text,
    ...neu.insetDeep,
  },

  ruleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 10,
    marginTop: 6,
    ...neu.raised,
  },
  rulePattern: { fontSize: 13, color: c.text, fontWeight: '600' },
  ruleMeta: { fontSize: 11, color: c.slate, marginTop: 2 },
  ruleFormCard: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 10,
    gap: 8,
    marginTop: 10,
    ...neu.raised,
  },

  decisionCard: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 12,
    gap: 4,
    ...neu.raised,
  },
  decisionHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  decisionFile: { flex: 1, fontSize: 13, fontWeight: '700', color: c.text },
  methodBadge: { backgroundColor: c.lime, borderRadius: radii.pill, paddingHorizontal: 8, paddingVertical: 2 },
  methodBadgeRule: { backgroundColor: t.goldGlow },
  methodBadgeText: { fontSize: 10, fontWeight: '800', color: c.blueText },
  decisionPath: { fontSize: 11, fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace', color: c.slate },
  decisionReason: { fontSize: 12, color: c.text },
  decisionMeta: { fontSize: 10, color: c.slate },

  pagerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    paddingVertical: 10,
  },
  pagerBtn: { backgroundColor: t.glass, borderRadius: radii.tile, paddingHorizontal: 12, paddingVertical: 6, ...neu.raisedSm },
  pagerBtnDisabled: { opacity: 0.4 },
  pagerBtnText: { fontSize: 12, fontWeight: '700', color: c.blueText },
  pagerLabel: { fontSize: 12, color: c.slate },
}),
);
